import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api.js";
import type { RevisionPass, RevisionTask } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";

interface Props {
  projectId: string;
  onOpenScene: (sceneId: string) => void;
}

const CATEGORY_LABEL: Record<string, string> = {
  manual: "Manual", continuity: "Continuity", promise: "Promise", prose: "Prose", intelligence: "Intelligence"
};

/**
 * Revision Mode — rounds of edits ("Draft 2 Revision") with deep-linked tasks.
 * Tasks are hand-written or auto-generated from Continuity findings + forgotten
 * threads. Distinct from scene version history.
 */
export function RevisionPanel({ projectId, onOpenScene }: Props) {
  const toast = useToast();
  const [passes, setPasses] = useState<RevisionPass[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [tasks, setTasks] = useState<RevisionTask[]>([]);
  const [newPassTitle, setNewPassTitle] = useState("");
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadPasses = useCallback(async () => {
    setLoading(true);
    try {
      const { passes } = await api.revisionPasses(projectId);
      setPasses(passes);
      setActiveId((cur) => cur && passes.some((p) => p.id === cur) ? cur : passes[0]?.id ?? null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't load revision passes");
    } finally {
      setLoading(false);
    }
  }, [projectId, toast]);

  const loadTasks = useCallback(async (passId: string) => {
    try { const { tasks } = await api.revisionTasks(projectId, passId); setTasks(tasks); }
    catch { setTasks([]); }
  }, [projectId]);

  useEffect(() => { void loadPasses(); }, [loadPasses]);
  useEffect(() => { if (activeId) void loadTasks(activeId); else setTasks([]); }, [activeId, loadTasks]);

  const active = passes.find((p) => p.id === activeId) ?? null;

  const createPass = async () => {
    const title = newPassTitle.trim();
    if (!title) return;
    try {
      const { pass } = await api.createRevisionPass(projectId, { title });
      setNewPassTitle("");
      await loadPasses();
      setActiveId(pass.id);
    } catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't create pass"); }
  };

  const generate = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const { added } = await api.generateRevisionTasks(projectId, active.id);
      toast.success(added > 0 ? `Added ${added} task${added === 1 ? "" : "s"} from continuity & story signals.` : "No new signals to add — you're on top of it.");
      await loadTasks(active.id);
      await loadPasses();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Generate failed"); }
    finally { setBusy(false); }
  };

  const addTask = async () => {
    if (!active) return;
    const title = newTaskTitle.trim();
    if (!title) return;
    try {
      await api.createRevisionTask(projectId, active.id, { title });
      setNewTaskTitle("");
      await loadTasks(active.id);
      await loadPasses();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't add task"); }
  };

  const setStatus = async (task: RevisionTask, status: RevisionTask["status"]) => {
    try {
      await api.updateRevisionTask(task.id, { status });
      setTasks((prev) => prev.map((t) => t.id === task.id ? { ...t, status } : t));
      await loadPasses();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Update failed"); }
  };

  const archivePass = async () => {
    if (!active) return;
    try { await api.updateRevisionPass(projectId, active.id, { status: active.status === "archived" ? "active" : "archived" }); await loadPasses(); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Update failed"); }
  };

  if (loading) return <div className="insights-loading"><Icon name="sparkles" size={22} className="spin" style={{ color: "var(--accent)" }} /></div>;

  const open = tasks.filter((t) => t.status === "open");
  const done = tasks.filter((t) => t.status === "done");
  const pct = tasks.length ? Math.round((done.length / tasks.length) * 100) : 0;

  return (
    <div className="revmode">
      <div className="revmode-passbar">
        {passes.map((p) => (
          <button key={p.id} className={`revmode-pass ${p.id === activeId ? "active" : ""} ${p.status === "archived" ? "is-archived" : ""}`} onClick={() => setActiveId(p.id)}>
            {p.title}
            <span className="revmode-pass-count">{p.doneCount ?? 0}/{p.taskCount ?? 0}</span>
          </button>
        ))}
        <div className="revmode-newpass">
          <input className="input" placeholder="New revision pass…" value={newPassTitle} onChange={(e) => setNewPassTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void createPass(); }} style={{ maxWidth: 200 }} />
          <button className="btn btn-sm" onClick={() => void createPass()} disabled={!newPassTitle.trim()}><Icon name="plus" size={13} /> Add</button>
        </div>
      </div>

      {!active ? (
        <div className="review-empty card"><Icon name="info" size={18} style={{ color: "var(--info)" }} /><span>Create a revision pass to organize a round of edits — e.g. "Draft 2 Revision".</span></div>
      ) : (
        <section className="card insight-card">
          <div className="insight-card-head">
            <Icon name="history" size={16} style={{ color: "var(--accent)" }} />
            <h3>{active.title}</h3>
            {active.status === "archived" && <span className="chip">archived</span>}
            <span className="spacer" />
            <button className="btn btn-sm btn-ghost" onClick={() => void generate()} disabled={busy}><Icon name={busy ? "clock" : "sparkles"} size={13} className={busy ? "spin" : ""} /> Generate from signals</button>
            <button className="btn btn-sm btn-ghost" onClick={() => void archivePass()}><Icon name="vault" size={13} /> {active.status === "archived" ? "Unarchive" : "Archive"}</button>
          </div>

          <div className="progress-track" style={{ margin: "2px 0 6px" }}><div className="progress-fill" style={{ width: `${pct}%` }} /></div>
          <p className="faint" style={{ fontSize: 12, margin: "0 0 12px" }}>{done.length} of {tasks.length} done · {pct}%</p>

          <div className="revmode-add">
            <input className="input" placeholder="Add a revision task…" value={newTaskTitle} onChange={(e) => setNewTaskTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void addTask(); }} />
            <button className="btn btn-sm btn-primary" onClick={() => void addTask()} disabled={!newTaskTitle.trim()}>Add task</button>
          </div>

          {tasks.length === 0 ? (
            <p className="muted insight-empty">No tasks yet. Add one, or "Generate from signals" to pull in open continuity issues and forgotten threads.</p>
          ) : (
            <ul className="revmode-tasks">
              {[...open, ...done].map((t) => (
                <li key={t.id} className={`revmode-task ${t.status === "done" ? "is-done" : ""}`}>
                  <button className="revmode-check" onClick={() => void setStatus(t, t.status === "done" ? "open" : "done")} title={t.status === "done" ? "Mark open" : "Mark done"}>
                    {t.status === "done" ? <Icon name="check" size={14} /> : <span className="revmode-dot" />}
                  </button>
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div className="revmode-task-title">{t.title}</div>
                    {t.detail && <div className="faint revmode-task-detail">{t.detail}</div>}
                  </div>
                  <span className={`chip revmode-cat-${t.category}`}>{CATEGORY_LABEL[t.category] ?? t.category}</span>
                  {t.sceneId && <button className="btn btn-sm btn-ghost" onClick={() => onOpenScene(t.sceneId!)}><Icon name="file" size={12} /> Open</button>}
                  <button className="btn btn-icon btn-ghost" title="Delete" onClick={() => void api.deleteRevisionTask(t.id).then(() => { setTasks((p) => p.filter((x) => x.id !== t.id)); void loadPasses(); })}><Icon name="trash" size={13} /></button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
